import { SearchOption } from '@/constants/node';
import { SystemNodeKindInput } from '@/graphql/codegen/graphql';
import { FoldersPageTableData } from '@/pages/FoldersPages/FoldersPage/useFoldersPageColumns';
import { useDataManagementPageStore } from '@/stores/dataManagementStore';
import { useRecentlyCreatedFolderNodeStore } from '@/stores/recentlyCreatedFolderNodesStore';
import { getShareLinkToken } from '@/utils/shareLink';
import { useMemo } from 'react';
import { useFetchSystemNodes } from './useFetchSystemNodes';
import { usePagination } from './usePagination';
import { useTransformAndFilterSystemNodes } from './useTransformAndFilterSystemNodes';

export const useFetchAndManageSystemNodes = () => {
  const recentlyCreatedFolderNodes = useRecentlyCreatedFolderNodeStore(
    state => state.recentlyCreatedFolderNodes,
  );
  const hasShareLinkToken = getShareLinkToken();

  const pageIndex = useDataManagementPageStore(state => state.pagination.pageIndex);
  const pageSize = useDataManagementPageStore(state => state.pagination.pageSize);
  const parentNodeId = useDataManagementPageStore(state => state.parentNodeId);
  const searchKeyword = useDataManagementPageStore(state => state.searchKeyword);
  const searchOption = useDataManagementPageStore(state => state.searchOption);

  const {
    systemNodes: fetchedSystemNodes,
    totalNumberOfFiles,
    isFetchingSystemNodes,
    refetchSystemNodes,
  } = useFetchSystemNodes(
    {
      parentNodeId: parentNodeId || null,
      pageSize,
      pageIndex,
      searchTerm: searchKeyword || undefined,
      includeMigratedData: !!hasShareLinkToken,
      nodeKinds: [
        SystemNodeKindInput.FolderNode,
        SystemNodeKindInput.FileNode,
        SystemNodeKindInput.LinkNode,
      ],
    },
    { keepPreviousData: true, enableRecursiveSearch: searchOption === SearchOption.RECURSIVE },
  );

  // code below will trigger only when a folder is created or anything is deleted/renamed
  const { transformedAndFilteredSystemNodes: transformedNewlyCreatedFolderNodes } =
    useTransformAndFilterSystemNodes(recentlyCreatedFolderNodes);

  // code below will run whenever a node is deleted, renamed or moved but not when a new folder is created
  const { transformedAndFilteredSystemNodes: transformedAndFilteredFetchedSystemNodes } =
    useTransformAndFilterSystemNodes(fetchedSystemNodes);

  // strip out newly created folder nodes from the fetched system nodes
  const strippedFetchedSystemNodes = transformedAndFilteredFetchedSystemNodes.filter(node => {
    for (const recentlyCreatedFolderNode of recentlyCreatedFolderNodes) {
      if (recentlyCreatedFolderNode.id === node.id) {
        return false;
      }
    }
    return true;
  });

  const { elementsForCurrentPage: paginatedSystemNodes, totalNumberOfPages } =
    usePagination<FoldersPageTableData>({
      elements: strippedFetchedSystemNodes,
      totalNumberOfElements: totalNumberOfFiles,
      currentPageIndex: pageIndex,
      pageSize,
    });

  const displaySystemNodes = useMemo(() => {
    return [...transformedNewlyCreatedFolderNodes, ...paginatedSystemNodes];
  }, [paginatedSystemNodes, transformedNewlyCreatedFolderNodes]);

  return {
    totalNumberOfPages,
    isFetchingSystemNodes,
    totalNumberOfFiles,
    displaySystemNodes,
    refetchSystemNodes,
  };
};
